
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { PlusCircle, ChevronDown, Calendar, ArrowUpDown, X } from 'lucide-react';
import Layout from '@/components/Layout';
import { toast } from '@/components/ui/use-toast';
import { useLocation } from 'react-router-dom';
import {
  QuoteItem,
  getStoredQuotes,
  storeQuotes,
  getStoredChildren,
  updateQuote
} from '@/utils/storage';
import { useTheme } from '@/context/ThemeContext';
import QuoteDialog from '@/components/quotes/QuoteDialog';
import QuoteCard from '@/components/quotes/QuoteCard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { logger } from '@/utils/logger';

const QUOTES_PER_PAGE = 6;

const Quotes = () => {
  const [quotes, setQuotes] = useState<QuoteItem[]>([]);
  const [filteredQuotes, setFilteredQuotes] = useState<QuoteItem[]>([]);
  const [children, setChildren] = useState<{ id: string; name: string }[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const { theme } = useTheme();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const highlightQuoteId = queryParams.get('highlight');
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const storedQuotes = await getStoredQuotes();
        setQuotes(storedQuotes);
        setFilteredQuotes(storedQuotes);
        logger.log("Loaded quotes:", storedQuotes.length);
        
        const storedChildren = await getStoredChildren();
        if (storedChildren.length > 0) {
          setChildren(storedChildren.map(child => ({ id: child.id, name: child.name })));
        }
        
        // If there's a highlighted quote, ensure the page is set correctly to show it
        if (highlightQuoteId) {
          const quoteIndex = storedQuotes.findIndex(quote => quote.id === highlightQuoteId);
          if (quoteIndex >= 0) {
            const pageNumber = Math.floor(quoteIndex / QUOTES_PER_PAGE) + 1;
            setCurrentPage(pageNumber);
            
            // Scroll to the highlighted quote after a short delay to ensure rendering
            setTimeout(() => {
              const element = document.getElementById(`quote-${highlightQuoteId}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.classList.add('highlight-pulse');
              }
            }, 300);
          }
        }
      } catch (error) {
        logger.error("Error loading data:", error);
        toast({
          title: "Error",
          description: "Failed to load quotes. Please refresh the page.",
          variant: "destructive"
        });
      }
    };
    
    fetchData();
  }, [highlightQuoteId]);
  
  useEffect(() => {
    let filtered = [...quotes];

    // Filter by child
    if (selectedChildId) {
      filtered = filtered.filter(quote => quote.childId === selectedChildId);
    }

    // Filter by date range
    if (dateFrom) {
      filtered = filtered.filter(quote => new Date(quote.date) >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(quote => new Date(quote.date) <= dateTo);
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    setFilteredQuotes(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [selectedChildId, dateFrom, dateTo, sortOrder, quotes]);
  
  // Calculate the displayed quotes based on current page
  const displayedQuotes = filteredQuotes.slice(0, currentPage * QUOTES_PER_PAGE);
  logger.log("Current page:", currentPage);
  logger.log("Quotes per page:", QUOTES_PER_PAGE);
  logger.log("Total quotes:", quotes.length);
  logger.log("Filtered quotes:", filteredQuotes.length);
  logger.log("Displayed quotes:", displayedQuotes.length);
  
  const loadMoreQuotes = () => {
    setCurrentPage(prev => prev + 1);
  };

  const handleQuoteAdded = (newQuoteItem: QuoteItem) => {
    const updatedQuotes = [...quotes, newQuoteItem];
    setQuotes(updatedQuotes);
    
    // Update filtered quotes if needed
    if (!selectedChildId || selectedChildId === newQuoteItem.childId) {
      setFilteredQuotes(prev => [...prev, newQuoteItem]);
    }
  };

  const handleQuoteUpdated = (updatedQuote: QuoteItem) => {
    const updatedQuotes = quotes.map(quote =>
      quote.id === updatedQuote.id ? updatedQuote : quote
    );
    setQuotes(updatedQuotes);

    // Update filtered quotes too
    if (!selectedChildId || selectedChildId === updatedQuote.childId) {
      setFilteredQuotes(prev =>
        prev.map(quote => quote.id === updatedQuote.id ? updatedQuote : quote)
      );
    } else if (selectedChildId !== updatedQuote.childId) {
      // Remove from filtered quotes if it no longer matches the filter
      setFilteredQuotes(prev =>
        prev.filter(quote => quote.id !== updatedQuote.id)
      );
    }
  };

  const handleQuoteDeleted = (quoteId: string) => {
    const updatedQuotes = quotes.filter(quote => quote.id !== quoteId);
    setQuotes(updatedQuotes);

    // Update filtered quotes too
    setFilteredQuotes(prev => prev.filter(quote => quote.id !== quoteId));
  };

  const handleClearFilter = () => {
    setSelectedChildId(null);
  };

  const handleSelectChild = (childId: string | null) => {
    setSelectedChildId(childId);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const getCurrentFilterLabel = () => {
    if (!selectedChildId) return "All Quotes";
    const selectedChild = children.find(child => child.id === selectedChildId);
    return selectedChild ? selectedChild.name : "All Quotes";
  };

  const clearAllFilters = () => {
    setSelectedChildId(null);
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  const hasActiveFilters = selectedChildId || dateFrom || dateTo;
  const hasMoreQuotes = filteredQuotes.length > displayedQuotes.length;

  return (
    <Layout>
      <div className="pb-16 md:pb-0">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">Quotes</h1>
          <QuoteDialog onQuoteAdded={handleQuoteAdded}>
            <Button className="rounded-full bg-primary text-white hover:bg-primary/90">
              <PlusCircle className="h-4 w-4 mr-1.5" />
              Add Quote
            </Button>
          </QuoteDialog>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {/* Child Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <span>{getCurrentFilterLabel()}</span>
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Filter by Child</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleSelectChild(null)}
                className={!selectedChildId ? "bg-accent" : ""}
              >
                All Quotes
              </DropdownMenuItem>
              {children.map(child => (
                <DropdownMenuItem
                  key={child.id}
                  onClick={() => handleSelectChild(child.id)}
                  className={selectedChildId === child.id ? "bg-accent" : ""}
                >
                  {child.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Date From Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "justify-start text-left font-normal",
                  !dateFrom && "text-muted-foreground"
                )}
              >
                <Calendar className="mr-2 h-4 w-4" />
                {dateFrom ? format(dateFrom, "PPP") : "From date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                mode="single"
                selected={dateFrom}
                onSelect={setDateFrom}
                initialFocus
              />
              {dateFrom && (
                <div className="p-3 border-t">
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => setDateFrom(undefined)}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>

          {/* Date To Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "justify-start text-left font-normal",
                  !dateTo && "text-muted-foreground"
                )}
              >
                <Calendar className="mr-2 h-4 w-4" />
                {dateTo ? format(dateTo, "PPP") : "To date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                mode="single"
                selected={dateTo}
                onSelect={setDateTo}
                initialFocus
              />
              {dateTo && (
                <div className="p-3 border-t">
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => setDateTo(undefined)}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>

          {/* Sort Order */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <ArrowUpDown className="h-3.5 w-3.5" />
                <span>{sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}</span>
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>Sort By</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setSortOrder('newest')}
                className={sortOrder === 'newest' ? "bg-accent" : ""}
              >
                Newest First
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setSortOrder('oldest')}
                className={sortOrder === 'oldest' ? "bg-accent" : ""}
              >
                Oldest First
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="flex items-center gap-1.5"
            >
              <X className="h-3.5 w-3.5" />
              Clear Filters
            </Button>
          )}
        </div>
        
        <div className="grid gap-6 grid-cols-1">
          {displayedQuotes.map((quote) => (
            <div
              key={quote.id}
              id={`quote-${quote.id}`}
              className={`${highlightQuoteId === quote.id ? 'highlight-quote' : ''}`}
            >
              <QuoteCard
                quote={quote}
                onQuoteUpdated={handleQuoteUpdated}
                onQuoteDeleted={handleQuoteDeleted}
              />
            </div>
          ))}
        </div>
        
        {hasMoreQuotes && (
          <div className="flex justify-center mt-8">
            <Button 
              onClick={loadMoreQuotes} 
              variant="outline" 
              className="rounded-full"
              size="lg"
            >
              Load More Quotes ({filteredQuotes.length - displayedQuotes.length} more)
            </Button>
          </div>
        )}
        
        {displayedQuotes.length === 0 && (
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              {selectedChildId ? 
                "No quotes found for this child. Add a new quote or clear the filter." : 
                "No quotes found. Add a new quote to get started!"}
            </p>
          </div>
        )}
      </div>
      <style>
        {`
        @keyframes highlight-pulse {
          0% { box-shadow: 0 0 0 0 rgba(var(--color-primary), 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(var(--color-primary), 0); }
          100% { box-shadow: 0 0 0 0 rgba(var(--color-primary), 0); }
        }
        
        .highlight-quote {
          animation: highlight-pulse 1.5s ease-in-out 1;
        }
        `}
      </style>
    </Layout>
  );
};

export default Quotes;
